import React from 'react';
import Layout from '../components/Layout';
import { TrendingUp, TrendingDown, CheckCircle, Clock, DollarSign, ArrowLeft, ArrowRight, Banknote, ArrowUpRight, ArrowDownRight, Home } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import StatCard from '../components/StatCard';
import { useAuth } from '../hooks/useAuth';
import transactionService from '../services/transactionService';
import incomeService from '../services/incomeService';
import installmentService from '../services/installmentService';

export default function DashboardPage() {
  const [filterMonth, setFilterMonth] = React.useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = React.useState(new Date().getFullYear());

  const { data: transactions = [] } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => transactionService.list(),
  });
  const { data: incomes = [] } = useQuery({
    queryKey: ['incomes'],
    queryFn: () => incomeService.list(),
  });
  const { data: installments = [] } = useQuery({
    queryKey: ['installments'],
    queryFn: () => installmentService.list(),
  });

  // Filtrar transações por mês/ano
  const filteredTransactions = React.useMemo(() => {
    return (transactions as any[]).filter((t) => {
      const date = t.dueDate || t.date ? new Date(t.dueDate || t.date) : null;
      const month = t.month != null ? Number(t.month) : (date ? date.getMonth() + 1 : null);
      const year = t.year != null ? Number(t.year) : (date ? date.getFullYear() : null);
      return month === filterMonth && year === filterYear;
    });
  }, [transactions, filterMonth, filterYear]);

  // Filtrar receitas por mês/ano
  const filteredIncomes = React.useMemo(() => {
    return (incomes as any[]).filter((inc) => {
      const month = inc.month || new Date(inc.date).getMonth() + 1;
      const year = inc.year || new Date(inc.date).getFullYear();
      return month === filterMonth && year === filterYear;
    });
  }, [incomes, filterMonth, filterYear]);

  // Calculate stats do período filtrado
  const totalIncome = filteredIncomes.reduce((sum, inc) => sum + (inc.amount || 0), 0);
  const totalExpenses = filteredTransactions
    .filter((t) => t.type === 'debit')
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalPaid = filteredTransactions
    .filter((t) => t.type === 'debit' && t.isPaid)
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalPending = filteredTransactions
    .filter((t) => t.type === 'debit' && !t.isPaid)
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  
  // Calcular total de "Parcelas Finalizadas" no mês filtrado
  // Critério: parcelamentos cuja ÚLTIMA parcela está no mês/ano filtrado (não exige estar paga)
  const finishedInstallmentsTotal = React.useMemo(() => {
    const getMY = (t: any): { m: number; y: number } | null => {
      const d = t?.dueDate || t?.date ? new Date(t.dueDate || t.date) : null;
      const m = t?.month != null ? Number(t.month) : (d ? d.getMonth() + 1 : null);
      const y = t?.year != null ? Number(t.year) : (d ? d.getFullYear() : null);
      return m && y ? { m, y } : null;
    };

    const toKey = (m: number, y: number) => y * 100 + m; // yyyymm para comparações

    return (installments as any[]).reduce((total, it) => {
      const txs: any[] = Array.isArray(it.transactions) ? it.transactions : [];
      let endMonth: number | null = null;
      let endYear: number | null = null;
      let lastAmount: number = Number(it.installmentValue || 0);

      if (txs.length) {
        // Obter a transação com maior data (última parcela)
        let lastKey = -Infinity;
        txs.forEach((t) => {
          const p = getMY(t);
          if (!p) return;
          const k = toKey(p.m, p.y);
          if (k > lastKey) {
            lastKey = k;
            endMonth = p.m;
            endYear = p.y;
            lastAmount = Number(t.amount || lastAmount);
          }
        });
      } else {
        // Fallback pelo início + quantidade
        const count = it.installmentCount != null ? Number(it.installmentCount) : 1;
        const sM = it.startMonth != null ? Number(it.startMonth) : 1;
        const sY = it.startYear != null ? Number(it.startYear) : new Date().getFullYear();
        const endDate = new Date(sY, sM - 1 + Math.max(0, count - 1), 1);
        endMonth = endDate.getMonth() + 1;
        endYear = endDate.getFullYear();
      }

      if (endMonth === filterMonth && endYear === filterYear) {
        return total + (lastAmount || 0);
      }
      return total;
    }, 0);
  }, [installments, filterMonth, filterYear]);
  
  const balance = totalIncome - totalExpenses;

  // Navegação entre meses
  const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  
  const goPrev = () => {
    if (filterMonth === 1) {
      setFilterMonth(12);
      setFilterYear(filterYear - 1);
    } else {
      setFilterMonth(filterMonth - 1);
    }
  };

  const goNext = () => {
    if (filterMonth === 12) {
      setFilterMonth(1);
      setFilterYear(filterYear + 1);
    } else {
      setFilterMonth(filterMonth + 1);
    }
  };

  // Ordenar transações por data de criação (mais recentes primeiro) e pegar as 8 últimas
  const recentTransactions = React.useMemo(() => {
    return [...(transactions as any[])].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.date).getTime();
      const dateB = new Date(b.createdAt || b.date).getTime();
      return dateB - dateA;
    }).slice(0, 8);
  }, [transactions]);

  const monthSummary = React.useMemo(() => [
    { name: 'Receitas', value: totalIncome, color: '#16a34a' },
    { name: 'Despesas', value: totalExpenses, color: '#ef4444' },
    { name: 'Pagos', value: totalPaid, color: '#2563eb' },
    { name: 'Parcelas Finalizadas', value: finishedInstallmentsTotal, color: '#9333ea' },
  ], [totalIncome, totalExpenses, totalPaid, finishedInstallmentsTotal]);

  const categoryExpenses = React.useMemo(() => {
    const totals = filteredTransactions
      .filter((tx) => tx.type === 'debit')
      .reduce((acc: Record<string, number>, tx) => {
        const cat = (tx.category && tx.category.trim()) || 'Sem categoria';
        acc[cat] = (acc[cat] || 0) + (tx.amount || 0);
        return acc;
      }, {});

    const sorted = Object.entries(totals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const maxSlices = 6;
    if (sorted.length <= maxSlices) return sorted;

    const main: { name: string; value: number }[] = [];
    let outrosTotal = 0;

    sorted.forEach((item, idx) => {
      const isOutros = item.name.toLowerCase() === 'outros';
      if (idx < maxSlices - 1 && !isOutros) {
        main.push(item);
      } else {
        outrosTotal += item.value;
      }
    });

    if (outrosTotal > 0) {
      main.push({ name: 'Outros', value: outrosTotal });
    }

    return main.length ? main : sorted;
  }, [filteredTransactions]);

  const palette = ['#2563eb', '#16a34a', '#f97316', '#ef4444', '#a855f7', '#0ea5e9', '#94a3b8'];

  const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <Layout title="Dashboard" icon={Home}>
      <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Header - oculto em mobile */}
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-4 flex-shrink-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-left">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
                  <p className="text-gray-600 text-sm mt-1">Visão geral das suas finanças</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-start md:justify-end gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
              <button
                onClick={goPrev}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
                aria-label="Mês anterior"
              >
                <ArrowLeft className="w-5 h-5 text-gray-900" />
              </button>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(Number(e.target.value))}
                className="h-10 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-semibold text-gray-800 bg-white appearance-none capitalize"
              >
                {monthNames.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="h-10 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-semibold text-gray-800 bg-white appearance-none"
              >
                {[2023, 2024, 2025, 2026].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                onClick={goNext}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
                aria-label="Próximo mês"
              >
                <ArrowRight className="w-5 h-5 text-gray-900" />
              </button>
            </div>
            <p className="text-xs text-gray-500 md:text-right">Mostrando dados de {monthNames[filterMonth - 1]} / {filterYear}</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 md:px-8 py-6 space-y-6 overflow-y-auto">
          {/* Mobile Filters */}
          <div className="md:hidden space-y-2">
            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
                aria-label="Mês anterior"
              >
                <ArrowLeft className="w-5 h-5 text-gray-900" />
              </button>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(Number(e.target.value))}
                className="flex-1 h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-semibold text-gray-800 bg-white appearance-none capitalize"
              >
                {monthNames.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="w-24 h-10 px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-semibold text-gray-800 bg-white appearance-none"
              >
                {[2023, 2024, 2025, 2026].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                onClick={goNext}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
                aria-label="Próximo mês"
              >
                <ArrowRight className="w-5 h-5 text-gray-900" />
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center">Mostrando dados de {monthNames[filterMonth - 1]} / {filterYear}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            <StatCard label="Receitas" value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalIncome)} icon={<TrendingUp className="w-5 h-5 text-green-600" />} color="green" />
            <StatCard label="Despesas Totais" value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalExpenses)} icon={<TrendingDown className="w-5 h-5 text-red-600" />} color="red" />
            <StatCard label="Pagos" value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalPaid)} icon={<CheckCircle className="w-5 h-5 text-blue-600" />} color="blue" />
            <StatCard label="Saldo" value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(balance)} icon={<DollarSign className="w-5 h-5 text-gray-700" />} color={balance >= 0 ? 'green' : 'red'} />
            <StatCard
              label="Parcelas Finalizadas"
              helperText="Parcelas que foram finalizadas no mês, facilitando visualizar valores disponíveis no próximo mês"
              value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finishedInstallmentsTotal)}
              icon={<CheckCircle className="w-5 h-5 text-purple-600" />}
              color="purple"
            />
          </div>

          {/* Analysis Section */}
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6 space-y-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold">Análise Financeira</h2>
              <p className="text-sm text-gray-600">Resumo do mês selecionado: {monthNames[filterMonth - 1]} / {filterYear}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-gray-100 rounded-lg p-3 md:p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Comparativo do mês</h3>
                    <p className="text-xs text-gray-500">Receitas, despesas, pagos e pendentes</p>
                  </div>
                </div>
                <div className="h-64 overflow-x-auto">
                  {monthSummary.some((s) => s.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthSummary} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} tick={{ fontSize: 11 }} />
                        <YAxis dataKey="name" type="category" tickLine={false} axisLine={{ stroke: '#e5e7eb' }} tick={{ fontSize: 12 }} width={65} />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                          {monthSummary.map((entry) => (
                            <Cell key={`cell-${entry.name}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500">Sem dados para o mês.</div>
                  )}
                </div>
              </div>

              <div className="border border-gray-100 rounded-lg p-3 md:p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Despesas por categoria</h3>
                    <p className="text-xs text-gray-500">Top categorias do mês</p>
                  </div>
                  <div className="text-xs text-gray-500">{categoryExpenses.length} categorias</div>
                </div>
                <div className="h-64">
                  {categoryExpenses.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={categoryExpenses} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2}>
                          {categoryExpenses.map((entry, idx) => (
                            <Cell key={entry.name} fill={palette[idx % palette.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend verticalAlign="bottom" height={32} iconSize={10} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500">Sem despesas registradas.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
              <h2 className="text-lg font-bold">Transações Recentes</h2>
              <Link to="/dashboard/transactions" className="text-blue-600 text-sm font-medium hover:underline">
                Ver todas →
              </Link>
            </div>
            <div className="space-y-2">
              {recentTransactions.map((tx) => (
                <div key={tx.id} className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded border-b last:border-b-0">
                  <div className={`relative w-11 h-11 rounded-full flex items-center justify-center ${tx.type === 'credit' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <Banknote className={`w-6 h-6 ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`} />
                    {tx.type === 'credit' ? (
                      <ArrowUpRight className="w-4 h-4 text-green-700 absolute -right-1 -bottom-1 bg-white rounded-full p-0.5 border border-green-100" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-700 absolute -right-1 -bottom-1 bg-white rounded-full p-0.5 border border-red-100" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500">{tx.category} • {new Date(tx.dueDate || tx.date).toLocaleDateString('pt-BR')}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold text-sm ${tx.type === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(tx.amount)}
                    </div>
                  </div>
                </div>
              ))}
              {recentTransactions.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">Nenhuma transação registrada</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
